import type { AlertsData } from '../../api/types.js';
import { SegmentedControl } from '../SegmentedControl/SegmentedControl.js';
import { color, fontSize, radius } from '../../theme/tokens.js';
import {
  TIMELINES,
  managerOptions,
  timelineUnavailable,
  type AlertFilters,
  type OfferingFilter,
  type Timeline,
} from './model.js';

/**
 * The filter bar (blueprint lines 1144–1172): Leader → Manager, Audit type, All/Nursing/Allied,
 * Timeline (FR-AL12, FR-AL24).
 *
 * Choosing a leader resets Manager to All and limits its options to that leader's managers; a
 * Timeline window the checked history cannot answer is disabled and says how far back it reaches.
 */

/**
 * Every audit the Audit page lists, under its sections and in its order (owner request 2026-10-19).
 * ⚠ The option list is wider than the chips: an audit that never flags, or flags nobody today, is
 * still an option — and says so on its face, so choosing it and getting an empty table is expected
 * rather than mistaken for a fault.
 */
export function auditSections(data: AlertsData): Array<[string, AlertsData['audits']]> {
  const out = new Map<string, AlertsData['audits']>();
  for (const a of data.audits) out.set(a.section, [...(out.get(a.section) ?? []), a]);
  return [...out.entries()];
}

export function auditOptionLabel(a: AlertsData['audits'][number]): string {
  const face = a.alertLabel ?? a.name;
  if (a.kind === 'reporting') return `${face} — reports only, never flags`;
  if (a.openFlags === 0) return `${face} — no open flags`;
  return `${face} (${a.openFlags})`;
}

const SELECT = {
  fontFamily: 'inherit',
  fontSize: fontSize.control,
  fontWeight: 600,
  color: color.inkBody,
  background: color.surfaceSubtle,
  border: `1px solid ${color.border}`,
  borderRadius: radius.lg,
  padding: '7px 10px',
  cursor: 'pointer',
} as const;

export function AlertsFilters({
  data,
  filters,
  onChange,
}: {
  data: AlertsData;
  filters: AlertFilters;
  onChange: (next: AlertFilters) => void;
}) {
  const set = (patch: Partial<AlertFilters>) => onChange({ ...filters, ...patch });
  const managers = managerOptions(data, filters.leader);
  const reach = data.history.reachableDays;

  return (
    <div
      style={{
        background: color.surface,
        border: `1px solid ${color.border}`,
        borderRadius: radius['5xl'],
        padding: '14px 18px',
        marginBottom: 16,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
      }}
    >
      <select
        aria-label="Leader"
        value={filters.leader}
        onChange={(e) => set({ leader: e.target.value, manager: 'all' })}
        style={SELECT}
      >
        <option value="all">All leaders</option>
        {data.leaders.map((l) => (
          <option key={l.leaderKey} value={l.leaderKey}>
            {l.name}
          </option>
        ))}
      </select>
      <select aria-label="Manager" value={filters.manager} onChange={(e) => set({ manager: e.target.value })} style={SELECT}>
        <option value="all">All managers</option>
        {managers.map((m) => (
          <option key={m.managerId} value={String(m.managerId)}>
            {m.name ?? `Manager ${m.managerId}`}
          </option>
        ))}
      </select>
      <select aria-label="Audit type" value={filters.audit} onChange={(e) => set({ audit: e.target.value })} style={SELECT}>
        <option value="all">All audit types</option>
        {auditSections(data).map(([section, audits]) => (
          <optgroup key={section} label={section}>
            {audits.map((a) => (
              <option key={a.id} value={a.id}>
                {auditOptionLabel(a)}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      <SegmentedControl<OfferingFilter>
        label="Recruiter type"
        value={filters.offering}
        onChange={(offering) => set({ offering })}
        options={[
          { value: 'all', label: 'All' },
          { value: 'nursing', label: 'Nursing' },
          { value: 'allied', label: 'Allied' },
        ]}
      />
      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, flex: 'none' }}>
        <span
          style={{
            fontSize: fontSize.caption,
            fontWeight: 600,
            letterSpacing: '.5px',
            color: color.inkFaint,
            textTransform: 'uppercase',
          }}
        >
          Timeline
        </span>
        <SegmentedControl<Timeline>
          label="Timeline"
          value={filters.timeline}
          onChange={(timeline) => set({ timeline })}
          options={TIMELINES.map((t) => {
            const disabled = timelineUnavailable(t, reach);
            return {
              value: t,
              label: t === 'all' ? 'All' : t,
              accessibleLabel:
                t === 'all' ? 'Timeline: all' : t === '1D' ? 'Timeline: flagged on the newest run' : `Timeline: flagged in the last ${t}`,
              disabled,
              title: disabled
                ? `History is checked back ${reach} day${reach === 1 ? '' : 's'} only, so a ${t} window cannot be answered yet.`
                : undefined,
            };
          })}
        />
      </div>
    </div>
  );
}
